import { useCallback, useEffect, useMemo, useState } from "react";
import { Stamp, Plus, Pencil, Trash2, X, CheckCircle2, Sparkles, Filter } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { OUTCOME_META, PURPOSES, CONFIDENCE_META } from "../components/DocumentChecker";

/* Admin manager for the visa_requirements database. Moderators create/edit curated
   records (source + last_verified + confidence are mandatory), request AI drafts
   (always saved as status=draft / confidence=unverified) and approve them. */

const OUTCOME_OPTIONS = Object.entries(OUTCOME_META).map(([v, m]) => [v, m.label]);
const PURPOSE_OPTIONS = [...PURPOSES, ["any", "Any purpose"]];
const PASSPORT_TYPE_OPTIONS = [["ordinary", "Ordinary"], ["diplomatic", "Diplomatic"], ["official", "Official"],
  ["refugee_document", "Refugee document"], ["any", "Any type"]];
const SOURCE_TYPES = ["government", "iata", "editorial", "ai_draft"];
const CONFIDENCES = ["verified", "high", "unverified"];
const VALIDITY_METHODS = [["", "—"], ["after_departure", "After departure"], ["after_entry", "After entry"], ["duration_of_stay", "Duration of stay"]];

const EMPTY = { nationality: "", destination: "", purpose: "tourism", passport_type: "ordinary",
  outcome: "visa_free", summary: "", max_stay: "", max_stay_days: "", visa_details: "",
  ea_name: "", ea_status: "", ea_url: "", ea_notes: "",
  passport_validity: "", passport_validity_months: "", validity_calculation_method: "",
  blank_pages: "", blank_pages_required: "", transit_notes: "", health_notes: "", customs_notes: "",
  immigration_notes: "", special_conditions: "",
  source: "", source_type: "government", confidence: "high",
  effective_date: "", last_verified: "", review_date: "", status: "draft" };

const inputCls = "w-full border border-zinc-300 px-3 py-2 text-sm mt-1 outline-none focus:ring-2 focus:ring-cyan-600";

const F = ({ label, children }) => (
  <div><label className="text-xs font-700 uppercase tracking-wide text-zinc-500">{label}</label>{children}</div>
);

export default function AdminVisaRequirements() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [countries, setCountries] = useState([]);
  const [filters, setFilters] = useState({ nationality: "", destination: "", status: "" });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draftForm, setDraftForm] = useState({ nationality: "", destination: "", purpose: "tourism" });
  const [drafting, setDrafting] = useState(false);

  const load = useCallback(() => Promise.all([
    api.get("/visas/requirements", { params: {
      nationality: filters.nationality || undefined, destination: filters.destination || undefined,
      status: filters.status || undefined } }).then((r) => setItems(r.data.items)),
    api.get("/visas/requirements/stats").then((r) => setStats(r.data)),
  ]).catch(() => toast.error("Could not load requirements")), [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/visas/countries").then((r) => setCountries(r.data.items)).catch(() => {}); }, []);

  const byIso = useMemo(() => Object.fromEntries(countries.map((c) => [c.iso2, c.name])), [countries]);

  const openEdit = (r) => setEditing(r
    ? { ...EMPTY, ...Object.fromEntries(Object.entries(r).filter(([k]) => k in EMPTY).map(([k, v]) => [k, v ?? ""])), id: r.id }
    : { ...EMPTY });

  const save = async () => {
    const body = { ...editing };
    delete body.id;
    if (!body.nationality || !body.destination) { toast.error("Nationality and destination are required"); return; }
    if (!body.source.trim()) { toast.error("Source is mandatory — cite where the rule was verified"); return; }
    setBusy(true);
    try {
      if (editing.id) await api.put(`/visas/requirements/${editing.id}`, body);
      else await api.post("/visas/requirements", body);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const approve = async (r) => {
    try { await api.post(`/visas/requirements/${r.id}/approve`); toast.success("Approved — now live in the checker"); load(); }
    catch { toast.error("Failed"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.nationality} → ${r.destination} (${r.purpose})?`)) return;
    try { await api.delete(`/visas/requirements/${r.id}`); load(); } catch { toast.error("Failed"); }
  };

  const requestDraft = async () => {
    if (!draftForm.nationality || !draftForm.destination) { toast.error("Pick nationality and destination"); return; }
    setDrafting(true);
    try {
      await api.post("/visas/requirements/draft", draftForm);
      toast.success("AI draft created (unverified) — review and approve it below");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Drafting failed"); }
    finally { setDrafting(false); }
  };

  const CSelect = ({ value, onChange, testid, placeholder = "Country…" }) => (
    <select value={value} onChange={onChange} data-testid={testid}
      className="border border-zinc-300 bg-white px-2 py-1.5 text-sm outline-none">
      <option value="">{placeholder}</option>
      {countries.map((c) => <option key={c.iso2} value={c.iso2}>{c.name}</option>)}
    </select>
  );

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10" data-testid="admin-visa-requirements">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight flex items-center gap-3">
          <Stamp className="w-9 h-9 text-cyan-700" /> Visa Requirements
        </h1>
        <button onClick={() => openEdit(null)} data-testid="visareq-new-btn"
          className="inline-flex items-center gap-1.5 bg-cyan-700 text-white px-4 py-2.5 text-sm font-700 hover:bg-cyan-600">
          <Plus className="w-4 h-4" /> New record
        </button>
      </div>
      <p className="text-muted-foreground mb-6 max-w-3xl">
        Structured entry-requirement records powering the Trip Document Checker. Every record must cite a
        <strong> source</strong> with <strong>last-verified date</strong> and <strong>confidence</strong>. AI drafts are always
        <strong> unverified</strong> and stay hidden until you approve them.
      </p>

      {stats && (
        <div className="flex flex-wrap gap-3 mb-6" data-testid="visareq-stats">
          {[["Approved", stats.approved], ["Drafts", stats.drafts], ["Nationalities", stats.nationalities], ["Destinations", stats.destinations]].map(([l, v]) => (
            <div key={l} className="border border-zinc-300 bg-white px-4 py-2.5 min-w-[110px]">
              <div className="font-heading font-900 text-xl text-cyan-700">{v}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">{l}</div>
            </div>
          ))}
        </div>
      )}

      <div className="border border-sky-300 bg-sky-50/60 p-4 mb-6" data-testid="visareq-draft-box">
        <div className="font-heading font-700 text-sm mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4 text-sky-700" /> AI-draft a missing route (saved as unverified draft for your review)</div>
        <div className="flex flex-wrap items-center gap-2">
          <CSelect value={draftForm.nationality} onChange={(e) => setDraftForm((s) => ({ ...s, nationality: e.target.value }))} testid="visareq-draft-nationality" placeholder="Passport country…" />
          <span className="text-zinc-400">→</span>
          <CSelect value={draftForm.destination} onChange={(e) => setDraftForm((s) => ({ ...s, destination: e.target.value }))} testid="visareq-draft-destination" placeholder="Destination…" />
          <select value={draftForm.purpose} onChange={(e) => setDraftForm((s) => ({ ...s, purpose: e.target.value }))} data-testid="visareq-draft-purpose"
            className="border border-zinc-300 bg-white px-2 py-1.5 text-sm outline-none">
            {PURPOSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button onClick={requestDraft} disabled={drafting} data-testid="visareq-draft-btn"
            className="inline-flex items-center gap-1.5 bg-sky-700 text-white px-4 py-1.5 text-sm font-700 hover:bg-sky-600 disabled:opacity-50">
            <Sparkles className="w-3.5 h-3.5" /> {drafting ? "Drafting…" : "Draft with AI"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="visareq-filters">
        <Filter className="w-4 h-4 text-zinc-400" />
        <CSelect value={filters.nationality} onChange={(e) => setFilters((s) => ({ ...s, nationality: e.target.value }))} testid="visareq-filter-nationality" placeholder="Any passport" />
        <CSelect value={filters.destination} onChange={(e) => setFilters((s) => ({ ...s, destination: e.target.value }))} testid="visareq-filter-destination" placeholder="Any destination" />
        <select value={filters.status} onChange={(e) => setFilters((s) => ({ ...s, status: e.target.value }))} data-testid="visareq-filter-status"
          className="border border-zinc-300 bg-white px-2 py-1.5 text-sm outline-none">
          <option value="">Any status</option><option value="approved">Approved</option><option value="draft">Draft</option>
        </select>
        <span className="text-xs text-muted-foreground">{items.length} records</span>
      </div>

      <div className="space-y-2" data-testid="visareq-list">
        {items.map((r) => {
          const om = OUTCOME_META[r.outcome] || {};
          const conf = CONFIDENCE_META[r.confidence] || CONFIDENCE_META.unverified;
          return (
            <div key={r.id} className="border border-zinc-300 bg-white p-3 flex flex-wrap items-center gap-2" data-testid={`visareq-row-${r.id}`}>
              <span className={`text-[10px] font-900 tracking-widest px-1.5 py-0.5 ${r.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {r.status === "approved" ? "LIVE" : "DRAFT"}
              </span>
              <span className="font-mono text-sm font-700">{r.nationality} → {r.destination}</span>
              <span className="text-xs text-muted-foreground hidden lg:inline">{byIso[r.nationality]} → {byIso[r.destination]}</span>
              <span className={`text-[10px] font-700 px-1.5 py-0.5 border ${om.cls || ""}`}>{om.label || r.outcome}</span>
              <span className="text-[10px] font-700 px-1.5 py-0.5 bg-zinc-100 text-zinc-600">{r.purpose}</span>
              <span className={`text-[10px] font-700 px-1.5 py-0.5 ${conf.cls}`}>{conf.label}</span>
              <span className="text-xs text-muted-foreground flex-1 min-w-[120px] truncate">{r.summary}</span>
              <span className="text-xs text-muted-foreground hidden md:inline">verified {r.last_verified}</span>
              {r.status !== "approved" && (
                <button onClick={() => approve(r)} data-testid={`visareq-approve-${r.id}`}
                  className="inline-flex items-center gap-1 bg-emerald-700 text-white px-2.5 py-1 text-xs font-700 hover:bg-emerald-600">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                </button>
              )}
              <button onClick={() => openEdit(r)} data-testid={`visareq-edit-${r.id}`} className="inline-flex items-center gap-1 text-xs font-700 text-cyan-700 hover:underline"><Pencil className="w-3.5 h-3.5" /> Edit</button>
              <button onClick={() => remove(r)} data-testid={`visareq-delete-${r.id}`} className="inline-flex items-center gap-1 text-xs font-700 text-rose-600 hover:underline"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            </div>
          );
        })}
        {items.length === 0 && <p className="text-sm text-muted-foreground">No records match these filters.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" data-testid="visareq-editor">
          <div className="bg-white border border-zinc-300 w-full max-w-4xl my-8">
            <div className="flex items-center justify-between px-5 py-3 bg-cyan-700 text-white">
              <span className="font-heading font-900">{editing.id ? "Edit requirement" : "New requirement"}</span>
              <button onClick={() => setEditing(null)} data-testid="visareq-editor-close" className="p-1 hover:bg-cyan-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <F label="Passport country (ISO2)"><input value={editing.nationality} onChange={(e) => setEditing((s) => ({ ...s, nationality: e.target.value.toUpperCase() }))} maxLength={2} data-testid="visareq-nationality-input" className={inputCls} placeholder="US" /></F>
                <F label="Destination (ISO2)"><input value={editing.destination} onChange={(e) => setEditing((s) => ({ ...s, destination: e.target.value.toUpperCase() }))} maxLength={2} data-testid="visareq-destination-input" className={inputCls} placeholder="DE" /></F>
                <F label="Purpose"><select value={editing.purpose} onChange={(e) => setEditing((s) => ({ ...s, purpose: e.target.value }))} data-testid="visareq-purpose-select" className={inputCls}>{PURPOSE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></F>
                <F label="Passport type"><select value={editing.passport_type} onChange={(e) => setEditing((s) => ({ ...s, passport_type: e.target.value }))} className={inputCls}>{PASSPORT_TYPE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></F>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <F label="Outcome"><select value={editing.outcome} onChange={(e) => setEditing((s) => ({ ...s, outcome: e.target.value }))} data-testid="visareq-outcome-select" className={inputCls}>{OUTCOME_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></F>
                <F label="Summary (one sentence)"><input value={editing.summary} onChange={(e) => setEditing((s) => ({ ...s, summary: e.target.value }))} data-testid="visareq-summary-input" className={inputCls} /></F>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <F label="Max stay (text)"><input value={editing.max_stay} onChange={(e) => setEditing((s) => ({ ...s, max_stay: e.target.value }))} className={inputCls} /></F>
                <F label="Max stay (days)"><input type="number" value={editing.max_stay_days} onChange={(e) => setEditing((s) => ({ ...s, max_stay_days: e.target.value }))} className={inputCls} /></F>
                <F label="EA name (ESTA/ETA/…)"><input value={editing.ea_name} onChange={(e) => setEditing((s) => ({ ...s, ea_name: e.target.value }))} className={inputCls} /></F>
                <F label="EA status"><select value={editing.ea_status} onChange={(e) => setEditing((s) => ({ ...s, ea_status: e.target.value }))} className={inputCls}><option value="">—</option><option value="required">Required</option><option value="expected">Expected</option><option value="not_required">Not required</option></select></F>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <F label="EA official URL"><input value={editing.ea_url} onChange={(e) => setEditing((s) => ({ ...s, ea_url: e.target.value }))} className={inputCls} /></F>
                <F label="EA notes"><input value={editing.ea_notes} onChange={(e) => setEditing((s) => ({ ...s, ea_notes: e.target.value }))} className={inputCls} /></F>
              </div>
              <F label="Visa details (type / how to apply)"><textarea rows={2} value={editing.visa_details} onChange={(e) => setEditing((s) => ({ ...s, visa_details: e.target.value }))} className={inputCls} /></F>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <F label="Passport validity (text)"><input value={editing.passport_validity} onChange={(e) => setEditing((s) => ({ ...s, passport_validity: e.target.value }))} className={inputCls} /></F>
                <F label="Validity (months)"><input type="number" step="0.5" value={editing.passport_validity_months} onChange={(e) => setEditing((s) => ({ ...s, passport_validity_months: e.target.value }))} className={inputCls} /></F>
                <F label="Counted from"><select value={editing.validity_calculation_method} onChange={(e) => setEditing((s) => ({ ...s, validity_calculation_method: e.target.value }))} className={inputCls}>{VALIDITY_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></F>
                <F label="Blank pages required"><input type="number" value={editing.blank_pages_required} onChange={(e) => setEditing((s) => ({ ...s, blank_pages_required: e.target.value }))} className={inputCls} /></F>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <F label="Transit notes"><textarea rows={2} value={editing.transit_notes} onChange={(e) => setEditing((s) => ({ ...s, transit_notes: e.target.value }))} className={inputCls} /></F>
                <F label="Special conditions"><textarea rows={2} value={editing.special_conditions} onChange={(e) => setEditing((s) => ({ ...s, special_conditions: e.target.value }))} className={inputCls} /></F>
                <F label="Health / vaccinations"><textarea rows={2} value={editing.health_notes} onChange={(e) => setEditing((s) => ({ ...s, health_notes: e.target.value }))} className={inputCls} /></F>
                <F label="Customs notes"><textarea rows={2} value={editing.customs_notes} onChange={(e) => setEditing((s) => ({ ...s, customs_notes: e.target.value }))} className={inputCls} /></F>
              </div>
              <F label="Immigration notes (funds, onward ticket, extensions)"><textarea rows={2} value={editing.immigration_notes} onChange={(e) => setEditing((s) => ({ ...s, immigration_notes: e.target.value }))} className={inputCls} /></F>
              <div className="border-t border-zinc-200 pt-3">
                <div className="text-xs font-900 uppercase tracking-widest text-zinc-500 mb-2">Mandatory metadata</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <F label="Source URL *"><input value={editing.source} onChange={(e) => setEditing((s) => ({ ...s, source: e.target.value }))} data-testid="visareq-source-input" className={inputCls} placeholder="https://official-government-source…" /></F>
                  <F label="Source type"><select value={editing.source_type} onChange={(e) => setEditing((s) => ({ ...s, source_type: e.target.value }))} className={inputCls}>{SOURCE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}</select></F>
                  <F label="Confidence"><select value={editing.confidence} onChange={(e) => setEditing((s) => ({ ...s, confidence: e.target.value }))} data-testid="visareq-confidence-select" className={inputCls}>{CONFIDENCES.map((v) => <option key={v} value={v}>{v}</option>)}</select></F>
                  <F label="Effective date"><input type="date" value={editing.effective_date} onChange={(e) => setEditing((s) => ({ ...s, effective_date: e.target.value }))} className={inputCls} /></F>
                  <F label="Last verified"><input type="date" value={editing.last_verified} onChange={(e) => setEditing((s) => ({ ...s, last_verified: e.target.value }))} data-testid="visareq-lastverified-input" className={inputCls} /></F>
                  <F label="Review by"><input type="date" value={editing.review_date} onChange={(e) => setEditing((s) => ({ ...s, review_date: e.target.value }))} className={inputCls} /></F>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-zinc-200">
                <label className="flex items-center gap-2 text-sm font-600 cursor-pointer">
                  <input type="checkbox" checked={editing.status === "approved"} onChange={(e) => setEditing((s) => ({ ...s, status: e.target.checked ? "approved" : "draft" }))}
                    data-testid="visareq-approved-checkbox" className="w-4 h-4 accent-cyan-700" />
                  Approved (live in the checker)
                </label>
                <button onClick={save} disabled={busy} data-testid="visareq-save-btn"
                  className="bg-emerald-700 text-white px-5 py-2.5 text-sm font-700 hover:bg-emerald-600 disabled:opacity-50">
                  {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
